import type { LogEntry } from "../API/Models";

/**
 * L'ACTION normalisée d'une entrée du journal (créé / modifié / déployé / dépublié / connexion…),
 * portée par la property `action` que le backend écrit désormais sur chaque entrée. Les ANCIENNES
 * entrées (titres anglais, sans property) restent affichables : l'action est alors déduite par
 * heuristique sur le logType + les préfixes anglais connus, avec repli « Modification ».
 */
export type ActivityActionKey =
	| "create"
	| "update"
	| "delete"
	| "restore"
	| "enable"
	| "disable"
	| "deploy-preview"
	| "deploy-production"
	| "remove-preview"
	| "remove-production"
	| "unpublish"
	| "auto-publish"
	| "auto-remove"
	| "widget-refresh"
	| "translate"
	| "login"
	| "forex"
	| "forex-sync"
	| "maintenance"
	| "purge"
	| "backup"
	| "rbac"
	| "user"
	| "storage";

export interface ActivityActionBadge {
	label: string;
	/** Chip Tailwind (fond clair + texte + bord) : palette réelle, pas de hex arbitraires. */
	chip: string;
}

/** Badge (libellé FR + couleurs de chip) par action. Palette imposée : create=émeraude, update=sky,
 *  deploy-preview=ambre, deploy-production=vert, unpublish=rose, delete=rouge, restore=teal,
 *  login=slate, forex=violet. */
export const ACTION_BADGES: Record<ActivityActionKey, ActivityActionBadge> = {
	create: { label: "Création", chip: "border-emerald-200 bg-emerald-50 text-emerald-700" },
	update: { label: "Modification", chip: "border-sky-200 bg-sky-50 text-sky-700" },
	delete: { label: "Suppression", chip: "border-red-200 bg-red-50 text-red-700" },
	restore: { label: "Restauration", chip: "border-teal-200 bg-teal-50 text-teal-700" },
	enable: { label: "Activation", chip: "border-emerald-200 bg-emerald-50 text-emerald-700" },
	disable: { label: "Désactivation", chip: "border-slate-200 bg-slate-100 text-slate-600" },
	"deploy-preview": { label: "Preview", chip: "border-amber-200 bg-amber-50 text-amber-700" },
	"deploy-production": { label: "Production", chip: "border-green-200 bg-green-50 text-green-700" },
	"remove-preview": { label: "Retrait preview", chip: "border-orange-200 bg-orange-50 text-orange-700" },
	"remove-production": { label: "Retrait production", chip: "border-rose-200 bg-rose-50 text-rose-700" },
	unpublish: { label: "Dépublication", chip: "border-rose-200 bg-rose-50 text-rose-700" },
	"auto-publish": { label: "Auto-publication", chip: "border-emerald-200 bg-emerald-50 text-emerald-700" },
	"auto-remove": { label: "Auto-retrait", chip: "border-rose-200 bg-rose-50 text-rose-700" },
	"widget-refresh": { label: "Widgets", chip: "border-slate-200 bg-slate-100 text-slate-600" },
	translate: { label: "Traduction", chip: "border-indigo-200 bg-indigo-50 text-indigo-700" },
	login: { label: "Connexion", chip: "border-slate-200 bg-slate-100 text-slate-600" },
	forex: { label: "Taux de change", chip: "border-violet-200 bg-violet-50 text-violet-700" },
	"forex-sync": { label: "Sync ForexAPI", chip: "border-violet-200 bg-violet-50 text-violet-700" },
	maintenance: { label: "Maintenance", chip: "border-slate-200 bg-slate-100 text-slate-600" },
	purge: { label: "Purge", chip: "border-red-200 bg-red-50 text-red-700" },
	backup: { label: "Sauvegarde", chip: "border-slate-200 bg-slate-100 text-slate-600" },
	rbac: { label: "Permissions", chip: "border-indigo-200 bg-indigo-50 text-indigo-700" },
	user: { label: "Utilisateur", chip: "border-slate-200 bg-slate-100 text-slate-600" },
	storage: { label: "Média", chip: "border-cyan-200 bg-cyan-50 text-cyan-700" },
};

const isKnownAction = (value: string): value is ActivityActionKey => value in ACTION_BADGES;

/**
 * Résout l'action d'une entrée : property `action` d'abord (nouvelles entrées), sinon heuristique
 * legacy : logType puis préfixes anglais des anciens messages : avec repli « update » (Modification).
 * Pure et sans dépendance React pour être testable.
 */
export const actionForLog = (
	entry: Pick<LogEntry, "type" | "message" | "properties">
): ActivityActionKey => {
	const prop = entry.properties?.["action"]?.trim().toLowerCase();
	if (prop && isKnownAction(prop)) return prop;

	const type = (entry.type ?? "").trim().toLowerCase();
	const msg = (entry.message ?? "").trim().toLowerCase();

	switch (type) {
		case "auth":
		case "user-login":
			return "login";
		case "forex-rates":
			return "forex";
		case "forex-sync":
			return "forex-sync";
		case "content-translate":
		case "translation":
			return "translate";
		case "content-unpublish-production":
			return "unpublish";
		case "content-auto-publish":
			return msg.startsWith("auto-removed") ? "auto-remove" : "auto-publish";
		case "content-widget-refresh":
			return "widget-refresh";
		case "deployment-promote":
			return "deploy-production";
		case "deployment-queue":
		case "deployment":
			return msg.includes("production") ? "deploy-production" : "deploy-preview";
		case "admin-maintenance":
			return "maintenance";
		case "archive-purge":
			return "purge";
		case "backup":
			return "backup";
		case "rbac":
			return "rbac";
		case "user-change":
			return "user";
		case "storage-upload":
		case "storage-compress":
			return "storage";
		case "content-deploy-preview":
			return msg.includes("removed from preview") ? "remove-preview" : "deploy-preview";
		case "content-deploy-production":
			return msg.includes("removed from production") ? "remove-production" : "deploy-production";
		case "content-update": {
			// Anciennes entrées anglaises : « Content created (…) », « Content deleted (…) », etc.
			if (msg.startsWith("content created")) return "create";
			if (msg.startsWith("content deleted")) return "delete";
			if (msg.startsWith("content restored")) return "restore";
			if (msg.startsWith("content disabled")) return "disable";
			if (msg.startsWith("content enabled")) return "enable";
			return "update";
		}
		default:
			return "update";
	}
};

/** Badge (libellé + chip) prêt à afficher pour une entrée du journal. Une entrée de niveau
 *  « error » (échec de sauvegarde, de sync…) prime sur le badge d'action et s'affiche en ROUGE,
 *  pour qu'un échec ne se noie jamais dans une pastille neutre grise. */
export const badgeForLog = (entry: Pick<LogEntry, "type" | "message" | "properties" | "level">): ActivityActionBadge => {
	const level = (entry.level ?? "").toLowerCase();
	if (level === "error") return { label: "Échec", chip: "border-rose-200 bg-rose-50 text-rose-700" };
	if (level === "warning") return { label: "Avertissement", chip: "border-amber-200 bg-amber-50 text-amber-700" };
	return ACTION_BADGES[actionForLog(entry)];
};

/** Etiquettes qui ne peuvent JAMAIS designer un type de contenu. */
const TAGS_NON_TYPE = new Set(["content", "page", "preview", "production", "deployment", "record", "records"]);

/**
 * Lien d'édition d'une entrée du journal.
 *
 * Source de vérité : les PROPRIÉTÉS de l'entrée (`schemaKey`, `contentId`), que le serveur
 * écrit sur chaque entrée de contenu. Les étiquettes, elles, arrivent TRIÉES ALPHABÉTIQUEMENT :
 * « celle qui suit content » est le plus souvent la langue, et c'est ainsi que « Toutes les
 * modifications récentes » a produit `/content/fr/<id>` puis « This content type is
 * unavailable » (incident du 28/08/2026).
 *
 * Repli pour les vieilles entrées sans propriétés : le type est déduit par ÉLIMINATION, et
 * seulement s'il ne reste qu'un seul candidat. Mieux vaut pas de lien qu'un lien faux.
 */
export const buildLogLink = (
	entree?: Pick<LogEntry, "tags" | "properties"> | Array<string> | null
): string | null => {
	const tags = Array.isArray(entree) ? entree : entree?.tags ?? [];
	const props = Array.isArray(entree) ? undefined : entree?.properties ?? undefined;
	if ((!tags || tags.length === 0) && !props) return null;

	const etiquettes = tags ?? [];
	const lang = (props?.language ?? etiquettes.find((t) => t && /^[a-z]{2}$/.test(t)) ?? "fr").trim();
	const slugTag = etiquettes.find((t) => t?.startsWith("slug:"));
	const slug = (props?.slug ?? (slugTag ? slugTag.slice(5) : "")).trim();
	const idTag = etiquettes.find((t) => t?.startsWith("id:"));
	const id = (props?.contentId ?? (idTag ? idTag.slice(3) : "")).trim();

	const schemaKey = (props?.schemaKey ?? "").trim();
	const estPage = schemaKey
		? schemaKey.toLowerCase() === "page"
		: etiquettes.includes("page");

	if (estPage) {
		if (slug) return `/page/${lang}/${slug}`;
		return id ? `/page/${encodeURIComponent(id)}` : null;
	}

	if (!id) return null;
	if (schemaKey) return `/content/${encodeURIComponent(schemaKey)}/${encodeURIComponent(id)}`;

	// Entrée ancienne, sans propriétés : élimination, jamais de pari sur la position.
	const candidats = etiquettes.filter(
		(t) => t && !TAGS_NON_TYPE.has(t) && !t.includes(":") && !/^[a-z]{2}$/.test(t)
	);
	if (candidats.length !== 1) return null;
	return `/content/${encodeURIComponent(candidats[0])}/${encodeURIComponent(id)}`;
};
